import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { buildTransactionColumns } from 'src/constants/payments-ops/transactionsGrid'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PaymentsOpsTransaction } from 'src/interfaces/transactionsReview'
import {
  getOutgoingTransactions,
  getOutgoingTransactionsLoading,
  getTransactionsReviewSubmitting,
} from 'src/selectors/transactionsReview'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'

interface Props {
  onViewDetails(transaction: PaymentsOpsTransaction): void
  onAuthorize(transaction: PaymentsOpsTransaction): void
}

const getRowId = (row: PaymentsOpsTransaction): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="Nothing pending authorization." />
}

const OutgoingTransactionsTableComponent = ({ onViewDetails, onAuthorize }: Props) => {
  const rows = useSelector(getOutgoingTransactions)
  const isLoading = useSelector(getOutgoingTransactionsLoading)
  const isSubmitting = useSelector(getTransactionsReviewSubmitting)
  const nameMap = usePartnerNameMap()
  const canAuthorize = useHasPermission(InternalPermission.TransactionsAuthorize)
  const { paginationModel, setPaginationModel } = useGridPagination(PAYMENTS_OPS_PAGE_SIZE_OPTIONS)

  const columns = useMemo(() => {
    return buildTransactionColumns({
      variant: 'outgoing',
      nameMap,
      onViewDetails,
      // Without `transactions_authorize` the Authorize column is dropped (spec §7.5).
      onAuthorize: canAuthorize ? onAuthorize : undefined,
      isSubmitting,
    })
  }, [canAuthorize, isSubmitting, nameMap, onAuthorize, onViewDetails])

  return (
    <GridTable<PaymentsOpsTransaction>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      EmptyElement={renderEmpty}
      tableDataTestId="outgoing-transactions-table"
      rowDataTestIdPrefix="outgoing-transactions"
    />
  )
}

export const OutgoingTransactionsTable = memo(OutgoingTransactionsTableComponent)
